import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { OidcSecurityService, autoLoginPartialRoutesGuardWithConfig } from 'angular-auth-oidc-client';

/**
 * Wraps autoLoginPartialRoutesGuardWithConfig and locks the configId to 'identityServer'.
 *
 * The app loads two OIDC configurations (identityServer + entraId). The base
 * AutoLoginPartialRoutesGuard without a configId calls getOpenIDConfiguration()
 * which defaults to the first config only, but the dual-config setup can cause
 * it to see an unauthenticated state and trigger a redirect loop.
 *
 * autoLoginPartialRoutesGuardWithConfig returns a functional guard that uses
 * inject() internally, so it must run inside an injection context — achieved
 * here via runInInjectionContext.
 *
 * Before falling through to a full authorize redirect, this guard attempts a
 * silent token refresh (forceRefreshSession). This prevents spurious redirects
 * when the access token has momentarily expired but a valid refresh token is
 * still available — a common occurrence when the browser throttles timers in a
 * background tab and the scheduled renewal fires late.
 */
@Injectable({ providedIn: 'root' })
export class AutoLoginGuard {
  private readonly guard = autoLoginPartialRoutesGuardWithConfig('identityServer');

  constructor(private injector: Injector) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    return runInInjectionContext(this.injector, () => {
      const oidcService = inject(OidcSecurityService);

      return oidcService.isAuthenticated('identityServer').pipe(
        take(1),
        switchMap(isAuthenticated => {
          if (isAuthenticated) {
            return of<boolean | UrlTree>(true);
          }

          // Access token may have expired while a valid refresh token still
          // exists (e.g., browser tab was backgrounded and the renewal timer
          // was throttled). Attempt a silent refresh before redirecting to the
          // identity server, so the user is not interrupted unnecessarily.
          const fallback = (): Observable<boolean | UrlTree> => runInInjectionContext(
            this.injector,
            () => this.guard(route, state) as Observable<boolean | UrlTree>,
          );

          return oidcService.forceRefreshSession(undefined, 'identityServer').pipe(
            take(1),
            switchMap(({ isAuthenticated: refreshed }) => {
              if (refreshed) {
                return of<boolean | UrlTree>(true);
              }
              // Refresh failed – let the standard guard save the URL and
              // redirect to the identity server.
              return fallback();
            }),
            catchError(() => fallback()),
          );
        }),
      );
    });
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    return this.canActivate(route, state);
  }
}
